// External
import React from "react";
import _ from "lodash";

/**
 * Creates title element for overridden grade options
 */
export const createOverriddenTitleElement = ({
  title,
  overriddenTitleClassName,
  overriddenDotClassName,
}) => (
  <span className={overriddenTitleClassName}>
    {title}
    <span className={overriddenDotClassName} />
  </span>
);

/**
 * Enhances grade option with override indicator if needed
 */
export const enhanceGradeOption = ({
  option,
  overriddenTitleClassName,
  overriddenDotClassName,
}) => {
  if (!option.isOverridden) {
    return { ...option };
  }

  return {
    ...option,
    titleElement: createOverriddenTitleElement({
      title: option.title,
      overriddenTitleClassName,
      overriddenDotClassName,
    }),
  };
};

/**
 * Creates section navigation node
 */
export const createSectionNode = ({ sectionNodeKey, t }) => ({
  key: sectionNodeKey,
  title: t("common:general_settings"),
  parent: "root",
  tier: 2,
  children: [],
});

/**
 * Creates grading periods settings node
 */
export const createGradingPeriodsSettingsNode = ({
  gradingPeriodsSettingsKey,
  sectionNodeKey,
  t,
}) => ({
  key: gradingPeriodsSettingsKey,
  title: t("common:grading_period_plural"),
  parent: sectionNodeKey,
  tier: 1,
  children: [],
});

/**
 * Creates default columns/rows node
 */
export const createDefaultColumnsRowsNode = ({
  defaultColumnsRowsKey,
  t,
  sectionNodeKey,
}) => ({
  key: defaultColumnsRowsKey,
  title: t("transcript:display_settings_default_columns_rows_heading"),
  parent: sectionNodeKey,
  tier: 1,
  children: [],
});

/**
 * Creates across year aggregates node
 */
export const createAcrossYearAggregatesNode = ({
  acrossYearAggregatesKey,
  t,
  sectionNodeKey,
}) => ({
  key: acrossYearAggregatesKey,
  title: t("transcript:display_settings_across_year_aggregates_heading"),
  parent: sectionNodeKey,
  tier: 1,
  children: [],
});

/**
 * Creates grade-wise configuration node
 */
export const createGradeWiseNode = ({
  gradeWiseNodeKey,
  defaultColumnsRowsKey,
  gradeWiseOptions,
  t,
}) => ({
  key: gradeWiseNodeKey,
  title: t("transcript:configure_columns_rows"),
  parent: "root",
  tier: 2,
  children: [defaultColumnsRowsKey, ..._.map(gradeWiseOptions, "key")],
});

/**
 * Builds complete navigation options tree
 */
export const buildNavigationOptions = ({
  sectionNodeKey,
  gradeWiseNodeKey,
  defaultColumnsRowsKey,
  acrossYearAggregatesKey,
  gradingPeriodsSettingsKey,
  gradeWiseOptions,
  overriddenTitleClassName,
  overriddenDotClassName,
  t,
}) => {
  const sectionNode = createSectionNode({ sectionNodeKey, t });
  sectionNode.children = [gradingPeriodsSettingsKey, acrossYearAggregatesKey];

  const enhancedGradeOptions = _.map(gradeWiseOptions, option =>
    enhanceGradeOption({
      option,
      overriddenTitleClassName,
      overriddenDotClassName,
    })
  );

  return [
    {
      key: "root",
      children: [sectionNodeKey, gradeWiseNodeKey],
    },
    sectionNode,
    createGradingPeriodsSettingsNode({
      gradingPeriodsSettingsKey,
      sectionNodeKey,
      t,
    }),
    createAcrossYearAggregatesNode({
      acrossYearAggregatesKey,
      t,
      sectionNodeKey,
    }),
    createDefaultColumnsRowsNode({
      defaultColumnsRowsKey,
      t,
      sectionNodeKey: gradeWiseNodeKey,
    }),
    createGradeWiseNode({
      gradeWiseNodeKey,
      defaultColumnsRowsKey,
      gradeWiseOptions,
      t,
    }),
    ...enhancedGradeOptions,
  ];
};
